import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)

  // Nuke stale auth state: called when refresh token is invalid (user deleted,
  // password changed elsewhere, etc.) to prevent supabase-js retry loops
  // that spam history.replaceState() via <Navigate>.
  const nukeStaleAuth = async () => {
    try { await supabase?.auth?.signOut({ scope: 'local' }) } catch { /* ignore */ }
    try {
      const keysToRemove = []
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i)
        if (k && (k.startsWith('sb-') || k === 'pending_verification_email' || k === 'rememberMe' || k === 'forgot_pw_cooldown_at' || k === 'resend_cooldown_at')) {
          keysToRemove.push(k)
        }
      }
      keysToRemove.forEach((k) => localStorage.removeItem(k))
      sessionStorage.clear()
      document.cookie = 'session_active=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT'
    } catch { /* ignore */ }
    setUser(null)
    setUserProfile(null)
    setProfileLoading(false)
    setLoading(false)
  }

  // Isolated async operations - never called from auth callbacks
  const profileOperations = {
    async load(userId) {
      if (!userId) return
      setProfileLoading(true)
      try {
        const { data, error } = await supabase?.from('user_profiles')
          ?.select('id, email, first_name, last_name, username, avatar_url, bio, role, status, subscription_tier, monthly_credits, remaining_credits, created_at, updated_at')
          ?.eq('id', userId)?.maybeSingle()

        // User was deleted or auth-rejected → nuke stale tokens to stop retry loop
        if (error?.code === 'PGRST301' || error?.message?.includes('JWT') || error?.message?.includes('403')) {
          await nukeStaleAuth()
          return
        }
        if (!error && !data) {
          // User row missing → deleted by admin → clear stale session
          await nukeStaleAuth()
          return
        }

        if (!error && data) {
          setUserProfile(data)
          // Auto-sync monthly_credits to subscription_plans via edge function
          // (user JWT cannot update monthly_credits directly due to trigger guard).
          // Skip for team_member — their credits are managed by team owner.
          if (data.subscription_tier !== 'team_member') {
            try {
              const { data: { session } } = await supabase.auth.getSession()
              if (session) {
                const res = await fetch(
                  `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/manage-credits`,
                  {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Bearer ${session.access_token}`,
                      'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
                    },
                    body: JSON.stringify({ action: 'sync' }),
                  }
                )
                const result = await res.json().catch(() => null)
                if (res.ok && result?.synced && result?.profile) {
                  setUserProfile(result.profile)
                }
              }
            } catch (syncErr) {
              if (import.meta.env?.DEV) console.error('Credits sync error:', syncErr)
            }
          }
        }
      } catch (error) {
        if (import.meta.env?.DEV) console.error('Profile load error:', error)
      } finally {
        setProfileLoading(false)
      }
    },

    clear() {
      setUserProfile(null)
      setProfileLoading(false)
    }
  }

  // Auth state handlers - PROTECTED from async modification
  const authStateHandlers = {
    // This handler MUST remain synchronous - Supabase requirement
    onChange: (event, session) => {
      setUser(session?.user ?? null)
      setLoading(false)

      if (session?.user) {
        profileOperations?.load(session?.user?.id) // Fire-and-forget
      } else {
        profileOperations?.clear()
        // เคลียร์ทุก key ที่เกี่ยว auth กรณี signOut ถูกเรียกตรงๆ (bypass signOut method)
        // ป้องกัน loop /email-verification ↔ /login-registration
        if (event === 'SIGNED_OUT') {
          try {
            localStorage.removeItem('pending_verification_email')
            localStorage.removeItem('rememberMe')
            localStorage.removeItem('forgot_pw_cooldown_at')
            localStorage.removeItem('resend_cooldown_at')
            document.cookie = 'session_active=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT'
          } catch { /* ignore */ }
        }
      }
    }
  }

  useEffect(() => {
    // Remember Me: check BEFORE loading session to avoid flash
    const isRemembered = localStorage.getItem('rememberMe') === 'true';
    const hasSessionCookie = document.cookie.includes('session_active=1');

    if (!isRemembered && !hasSessionCookie) {
      // Browser was closed or first visit → sign out if session exists, skip getSession
      supabase?.auth?.getSession()?.then(({ data: { session }, error }) => {
        if (error) {
          // Refresh token invalid/revoked (user deleted, etc.) → nuke stale state
          nukeStaleAuth();
          return;
        }
        if (session) {
          supabase?.auth?.signOut({ scope: 'local' }).then(() => setLoading(false));
        } else {
          setLoading(false);
        }
      }).catch(() => nukeStaleAuth());
    } else {
      // Initial session check
      supabase?.auth?.getSession()?.then(({ data: { session }, error }) => {
        if (error) {
          nukeStaleAuth();
          return;
        }
        authStateHandlers?.onChange(null, session)
      }).catch(() => nukeStaleAuth())
    }

    // CRITICAL: Always subscribe so new logins are detected
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      authStateHandlers?.onChange
    )

    return () => subscription?.unsubscribe()
  }, []);

  // Auth methods
  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({ email, password })
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut({ scope: 'local' })
      if (!error) {
        setUser(null)
        profileOperations?.clear()
        // Clear all auth-related localStorage keys on logout
        localStorage.removeItem('pending_verification_email')
        localStorage.removeItem('rememberMe')
        localStorage.removeItem('forgot_pw_cooldown_at')
        localStorage.removeItem('resend_cooldown_at')
        // Also wipe any leftover supabase tokens from the other storage backend
        try { sessionStorage.clear() } catch { /* ignore */ }
        document.cookie = 'session_active=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
      }
      return { error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } }
    
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', user?.id)?.select()?.single()
      if (!error) setUserProfile(data)
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const refreshProfile = async () => {
    if (user) await profileOperations.load(user.id)
  }

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    signIn,
    signOut,
    updateProfile,
    refreshProfile,
    isAuthenticated: !!user
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
